import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/i18n";
import { switchLanguage } from "@/lib/lang";

const FULL_NAME = { nl: "Nederlands", en: "English", fr: "Français", de: "Deutsch" };

// Refined, header-friendly language switcher: a compact pill (globe + current code)
// that opens an elegant glass dropdown with the active language marked.
export const HeaderLangSwitch = () => {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "nl").slice(0, 2);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const label = (LANGUAGES.find((l) => l.code === current) || { label: current.toUpperCase() }).label;

  return (
    <div className="relative" ref={ref}>
      <motion.button
        whileHover={{ scale: 1.06, y: -1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 250, damping: 26 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="header-lang-toggle"
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`group inline-flex items-center gap-0.5 rounded-full px-1.5 py-1 text-sm font-medium transition-colors duration-200 ${
          open ? "text-gold" : "text-zinc-900 hover:text-gold"
        }`}
      >
        <span className="tracking-wide">{label}</span>
        <ChevronDown
          strokeWidth={1.8}
          className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180 text-gold" : "text-zinc-400"}`}
        />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="listbox"
            data-testid="header-lang-menu"
            className="absolute right-0 mt-2 w-52 rounded-2xl bg-white/95 backdrop-blur-xl border border-zinc-200/70 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.3)] overflow-hidden z-50 p-1.5"
          >
            <p className="px-3 pt-1.5 pb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">
              Taal · Language
            </p>
            {LANGUAGES.map((l, i) => {
              const active = current === l.code;
              return (
                <motion.button
                  key={l.code}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.035 * i, duration: 0.18 }}
                  role="option"
                  aria-selected={active}
                  data-testid={`header-lang-${l.code}`}
                  onClick={() => switchLanguage(l.code)}
                  className={`w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                    active ? "bg-amber-50 text-zinc-900" : "text-zinc-600 hover:bg-zinc-100/80 hover:text-zinc-900"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span className={`w-6 text-[11px] font-semibold tracking-wide ${active ? "text-gold" : "text-zinc-400"}`}>
                      {l.label}
                    </span>
                    <span className="font-medium">{FULL_NAME[l.code] || l.label}</span>
                  </span>
                  {active && <Check strokeWidth={2.4} className="w-4 h-4 text-gold shrink-0" />}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
